/**
 * `Segmented` (W1-12 ui-kit-i18n; contract SegmentedProps): a single-choice segmented control following the
 * WAI-ARIA radio group pattern (role="radiogroup" with role="radio" items).
 *  - One tab stop: Tab enters on the checked segment (the first one while the value is mixed) and leaves the
 *    group; while focus is inside, the stop stays on the focused segment.
 *  - Left / Right move focus AND pick (wrapping), as screen-reader users expect from radios; Up / Down do the
 *    same unless the group sits in a vertical toolbar, which uses them to move between its items.
 *  - Click and Space pick the focused segment; picking the checked one again keeps it (a radio group cannot be
 *    emptied). 'mixed' shows no segment as checked.
 *  - Options with a disabled reason stay focusable (aria-disabled) so arrows can reach them and their tooltip
 *    explains why; they are never picked.
 */

import { useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTr } from './bridge';
import { Icon } from './Icon';
import { cx } from './internal/cx';
import f from './internal/form.module.css';
import s from './Segmented.module.css';
import { Tooltip } from './Tooltip';
import type { SegmentedProps } from './types';

export interface SegmentedExtraProps {
  /** Accessible name when there is no visible label. */
  readonly ariaLabel?: string | undefined;
  readonly className?: string | undefined;
  readonly size?: 'sm' | 'md' | undefined;
  /**
   * Narrow containers (W3-06 CR-2, UX-057 / UX-051): the option label becomes visually hidden (still the
   * accessible name) and is shown as the item's tooltip. `'auto'` switches by a container query on the group.
   */
  readonly iconOnly?: boolean | 'auto' | undefined;
}

/** True when the group is an item of a vertical toolbar (Up / Down belong to the toolbar there). */
function inVerticalToolbar(el: HTMLElement): boolean {
  return el.parentElement?.closest('[role="toolbar"]')?.getAttribute('aria-orientation') === 'vertical';
}

export function Segmented<T extends string>(props: SegmentedProps<T> & SegmentedExtraProps) {
  const tr = useTr();
  const labelId = useId();
  const items = useRef(new Map<T, HTMLButtonElement>());
  const [focusedValue, setFocusedValue] = useState<T | null>(null);
  const hasLabel = props.label !== undefined && props.label !== null;
  const { options, value } = props;

  const enabled = (v: T): boolean => options.find((o) => o.value === v)?.disabledReason === undefined;
  const checkedValue = value !== 'mixed' && options.some((o) => o.value === value) ? value : null;
  const focusedOption = focusedValue !== null && options.some((o) => o.value === focusedValue) ? focusedValue : null;
  const tabStop = focusedOption ?? checkedValue ?? options[0]?.value ?? null;

  const pick = (v: T): void => {
    if (v !== value && enabled(v)) props.onChange(v);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || options.length === 0) return;
    const vertical = e.key === 'ArrowUp' || e.key === 'ArrowDown';
    const horizontal = e.key === 'ArrowLeft' || e.key === 'ArrowRight';
    if (!horizontal && !(vertical && !inVerticalToolbar(e.currentTarget))) return;
    const from = options.findIndex((o) => items.current.get(o.value) === e.target);
    if (from < 0) return;
    e.preventDefault();
    const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
    const forward = e.key === 'ArrowDown' || (e.key === 'ArrowRight') !== rtl;
    const next = options[(from + (forward ? 1 : -1) + options.length) % options.length];
    if (next === undefined) return;
    items.current.get(next.value)?.focus();
    pick(next.value);
  };

  return (
    <div className={cx(f.control, props.className)}>
      {hasLabel && (
        <span id={labelId} className={f.label}>
          {props.label}
        </span>
      )}
      <div
        role="radiogroup"
        className={cx(s.group, props.size === 'sm' && s.sm)}
        aria-labelledby={hasLabel ? labelId : undefined}
        aria-label={hasLabel ? undefined : props.ariaLabel}
        data-mixed={value === 'mixed' ? 'true' : undefined}
        data-icon-only={props.iconOnly === 'auto' ? 'auto' : props.iconOnly === true ? 'true' : undefined}
        onKeyDown={onKeyDown}
        onBlur={(e) => {
          if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) setFocusedValue(null);
        }}
      >
        {options.map((o) => {
          const reason = o.disabledReason === undefined ? null : tr(o.disabledReason);
          const checked = o.value === checkedValue;
          const iconOnly = props.iconOnly !== undefined;
          const item = (
            <button
              key={o.value}
              ref={(el) => {
                if (el === null) items.current.delete(o.value);
                else items.current.set(o.value, el);
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              aria-disabled={reason !== null ? true : undefined}
              tabIndex={o.value === tabStop ? 0 : -1}
              data-state={checked ? 'on' : 'off'}
              className={cx(s.item, reason !== null && s.disabled)}
              onFocus={() => {
                setFocusedValue(o.value);
              }}
              onClick={() => {
                pick(o.value);
              }}
            >
              {o.icon !== undefined && <Icon name={o.icon} size={14} />}
              <span className={s.label}>{o.label}</span>
            </button>
          );
          // The tooltip: the disabled reason when there is one, else the option's name while its label is hidden.
          const tip = reason ?? (iconOnly ? o.label : null);
          return tip === null ? (
            item
          ) : (
            <Tooltip key={o.value} content={tip}>
              {item}
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
